import React from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'

const PrivacyPolicyPage = () => {
  return (
    <div>
      <PageMeta 
        title="Privacy Policy | RDS Studio"
        description="Learn how RDS Studio collects, uses, and protects your personal information. Our commitment to privacy and data security."
        canonical="https://rdsstudio.co.za/privacy-policy"
      />
      <Header />
      <main className="main-content">
        <section className="section legal-page">
          <div className="section-inner">
            <h1 className="title title-h2">Privacy Policy</h1>
            <p className="description" style={{ marginBottom: '2rem' }}>
              Last updated: December 11, 2025
            </p>

            <div className="legal-content">
              <h2>1. Introduction</h2>
              <p>
                RDS Studio ("we", "us", "our", or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>https://rdsstudio.co.za</strong> (the "Site") and use our services.
              </p>
              <p>
                Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Site or services.
              </p>

              <h2>2. Information We Collect</h2>
              
              <h3>2.1 Information You Provide Directly</h3>
              <ul>
                <li><strong>Contact Form Data:</strong> When you submit a contact form, we collect your name, email address, phone number, company name, and message content.</li>
                <li><strong>Newsletter Subscription:</strong> When you subscribe to our newsletter, we collect your email address.</li>
                <li><strong>Discovery Call Requests:</strong> When booking a discovery call, we collect your name, email, phone number, company information, and project details.</li>
                <li><strong>Service Inquiries:</strong> Any communication with our team may include additional information you choose to share.</li>
              </ul>

              <h3>2.2 Information Collected Automatically</h3>
              <ul>
                <li><strong>Log Data:</strong> Our servers automatically log information including IP address, browser type, referring/exit pages, operating system, date/time stamps, and clickstream data.</li>
                <li><strong>Cookies & Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to remember your preferences, understand site usage, and improve user experience.</li>
                <li><strong>Analytics:</strong> We use Google Analytics to track visitor behavior, page performance, and traffic sources. You can opt-out by disabling JavaScript or using browser extensions.</li>
                <li><strong>Device Information:</strong> We collect device type, screen resolution, and browser language to optimize our Site.</li>
              </ul>

              <h2>3. How We Use Your Information</h2>
              <p>We use collected information for:</p>
              <ul>
                <li>Responding to your inquiries and providing requested services</li>
                <li>Sending newsletters and marketing communications (with your consent)</li>
                <li>Processing and fulfilling project requests</li>
                <li>Improving our Site, services, and user experience</li>
                <li>Analyzing usage patterns and optimizing performance</li>
                <li>Detecting and preventing fraud, security breaches, and technical issues</li>
                <li>Complying with legal obligations</li>
                <li>Personalizing content and recommendations</li>
              </ul>

              <h2>4. Legal Basis for Processing (GDPR Compliance)</h2>
              <p>For users in the European Union, we process personal data under the following legal bases:</p>
              <ul>
                <li><strong>Consent:</strong> When you explicitly opt-in to newsletters or marketing</li>
                <li><strong>Contract Performance:</strong> Processing necessary to fulfill service agreements</li>
                <li><strong>Legitimate Interests:</strong> Improving services, security, and analytics</li>
                <li><strong>Legal Obligation:</strong> Compliance with applicable laws and regulations</li>
              </ul>

              <h2>5. Information Sharing & Disclosure</h2>
              
              <h3>5.1 Third-Party Service Providers</h3>
              <p>We may share your information with trusted third parties who assist us:</p>
              <ul>
                <li><strong>Email Service Providers:</strong> For newsletter delivery and email communications</li>
                <li><strong>CRM Systems:</strong> For managing client relationships and project tracking</li>
                <li><strong>Analytics Providers:</strong> Google Analytics for traffic and behavior analysis</li>
                <li><strong>Payment Processors:</strong> For secure payment processing (if applicable)</li>
                <li><strong>Hosting Providers:</strong> For Site infrastructure and data storage</li>
              </ul>

              <h3>5.2 Legal Requirements</h3>
              <p>We may disclose information when required by law, court order, regulatory request, or to protect our legal rights and safety.</p>

              <h3>5.3 Business Transfers</h3>
              <p>If RDS Studio is involved in a merger, acquisition, bankruptcy, or asset sale, your information may be transferred as part of that transaction.</p>

              <h3>5.4 No Sale of Personal Data</h3>
              <p>We do not sell, trade, or rent your personal information to third parties for marketing purposes.</p>

              <h2>6. Data Security</h2>
              <p>
                We implement industry-standard security measures including SSL encryption, secure servers, access controls, and regular security audits to protect your information from unauthorized access, alteration, and disclosure.
              </p>
              <p>
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.
              </p>

              <h2>7. Data Retention</h2>
              <ul>
                <li><strong>Contact Form Data:</strong> Retained for 2 years or until request for deletion</li>
                <li><strong>Newsletter Subscribers:</strong> Retained until unsubscribe</li>
                <li><strong>Project/Client Data:</strong> Retained for project duration plus 7 years (legal compliance)</li>
                <li><strong>Log Data:</strong> Retained for 90 days</li>
                <li><strong>Analytics Data:</strong> Retained per Google Analytics default policy (26 months)</li>
              </ul>

              <h2>8. Your Rights & Choices</h2>
              
              <h3>8.1 Access & Portability</h3>
              <p>You have the right to request a copy of your personal data we hold and to receive it in a portable format.</p>

              <h3>8.2 Correction & Deletion</h3>
              <p>You can request correction of inaccurate information or deletion of your data (subject to legal obligations).</p>

              <h3>8.3 Opt-Out of Marketing</h3>
              <p>You can unsubscribe from newsletters by clicking the "unsubscribe" link in any email or contacting us directly.</p>

              <h3>8.4 Cookie Management</h3>
              <p>You can disable cookies in your browser settings. Note that some Site features may not function properly with cookies disabled.</p>

              <h3>8.5 Do Not Track</h3>
              <p>Some browsers include a "Do Not Track" feature. Our Site currently does not respond to DNT signals, but you can manage tracking via browser settings.</p>

              <h2>9. Third-Party Links</h2>
              <p>
                Our Site may contain links to external websites. We are not responsible for the privacy practices of third-party sites. Please review their privacy policies before providing information.
              </p>

              <h2>10. Children's Privacy</h2>
              <p>
                Our Site and services are not intended for individuals under 18 years old. We do not knowingly collect personal information from children. If we become aware of such collection, we will promptly delete the information.
              </p>

              <h2>11. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries outside your country of residence, including the United States and European Union. These countries may have different data protection laws. By using our Site, you consent to such transfers.
              </p>

              <h2>12. South African Data Protection</h2>
              <p>
                As a South African company, we comply with the Protection of Personal Information Act (POPIA) and ensure appropriate safeguards for personal information processing within South Africa's legal framework.
              </p>

              <h2>13. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy periodically. Changes become effective when posted to the Site. Continued use of our Site constitutes acceptance of updated terms. We recommend reviewing this policy regularly.
              </p>

              <h2>14. Contact Us</h2>
              <p>
                If you have questions, concerns, or wish to exercise your privacy rights, please contact us:
              </p>
              <ul>
                <li><strong>Email:</strong> <a href="mailto:privacy@rdsstudio.co.za">privacy@rdsstudio.co.za</a></li>
                <li><strong>Address:</strong> Centurion, South Africa</li>
                <li><strong>Website:</strong> <a href="https://rdsstudio.co.za">https://rdsstudio.co.za</a></li>
              </ul>
              <p>
                We will respond to privacy requests within 30 days in compliance with applicable regulations.
              </p>

              <h2>15. Data Protection Officer (DPO)</h2>
              <p>
                For GDPR-related inquiries, you may contact our Data Protection Officer or submit requests through our contact form with "Data Protection Request" in the subject line.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default PrivacyPolicyPage
